/**
 * **La chambre** — l'accueil du jeu (avenant **A-7**).
 *
 * « La chambre n'est pas une alternative aux écrans : elle en est le CADRE. » C'est
 * l'endroit où l'on *est*. Les objets du décor lancent les actions (décision 2), et surtout
 * **l'escalier A-5 devient physique** (décision 5) : chaque déverrouillage fait apparaître un
 * objet. Le joueur ne lit pas « mécanique débloquée » — il voit sa chambre se remplir.
 *
 * Deux garde-fous de l'avenant sont tenus ailleurs, et c'est volontaire :
 * - la **barre d'actions permanente** (décision 3, « non négociable ») vit dans `Studio` :
 *   la chambre sert à VOIR, jamais à chasser un pixel pour aller dormir ;
 * - les **jauges, tendances et notifications** restent lisibles en permanence (décision 6).
 *
 * ⟦A-6⟧ Tout est en **couches CSS/SVG doodle** : mur en papier, plancher au marqueur, objets
 * en emoji dans des cadres tracés à la main. Zéro image, zéro génération — et le jour où les
 * doodles arrivent, on remplace l'emoji d'un objet sans toucher au reste.
 */

import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";

import { bandeDeLaChaine, hasContentFlag, type GameState } from "@maxime/shared";

// Aliasé : `contenu` est déjà le nom d'une variable locale dans la boucle des objets.
import { contenu as contenuJeu } from "../../game/content";
import {
  calqueDecoChambreN1,
  calquePlaqueChambreN1,
  calquePoseChambreN1,
  calquesLiegeChambreN1,
  calqueTendancesChambreN1,
  couleurCoinChambre,
  decalageAntiChevauchementBoite,
  echellePieceMobile,
  type EmpriseObjet,
  empriseObjetChambre,
  estPoseEtat,
  fondChambreN1,
  hotspotChambre,
  type IdDecorPalier,
  IDS_DECOR_PALIER,
  niveauChambre,
  nombrePostItsDisponibles,
  nomScene,
  poseEtatDeBase,
  poseEtatDeObjet,
  positionDansBoite,
  positionEcranBureau,
  POSES_ETAT,
  rectangleImageAffichee,
  sceneChambre,
  scenePossedePlanchesEtat,
  styleEmpriseHotspot,
  type AjustementScene,
  type AncrageVertical,
  type PoseAffichee,
} from "../../game/chambre";
import { ouvert } from "../../game/escalier";
import { badgeAlignement, estNuit, nombre, zoneSM } from "../../game/format";
import { estObjetAvecSuivi, spriteHeroSurvol, spriteHeros, type PoseHeros } from "../../game/heros";

export interface ObjetChambre {
  id: string;
  /** Ce qui s'affiche : emoji placeholder (§8.1) — remplaçable par un doodle en M4.
      ⟦Micro-correctif reskin 8-bit⟧ Optionnel depuis que le fond R1/R2 dessine LUI-MÊME
      certains objets (bureau, lit) : pour ceux-là il n'y a plus rien à poser par-dessus — le
      champ est absent, pas juste caché, et l'objet devient un hotspot invisible (voir
      `objet-${id}` dans app.css). */
  emoji?: string;
  /** ⟦C31⟧ Le nom de l'objet — ne s'affiche plus dans la scène (décision de Matt du 16/09,
      « pas besoin des étiquettes de lit sur la zone de jeu », les boutons d'action du bas
      portent déjà le nom et le coût). Sert désormais uniquement de nom accessible
      (`aria-label`, `libelleAccessibleObjet` plus bas) — jamais un texte visible en jeu. */
  nom: string;
  /** Position en % de la scène (coin haut-gauche de l'objet). */
  x: number;
  y: number;
  /** Taille relative — un lit n'est pas un téléphone. */
  taille?: number;
  /** Action au clic. Absent = objet purement décoratif (il se regarde). */
  action?: () => void;
  /** Ce que l'objet raconte quand on le survole — et son contenu lisible en place. */
  detail?: ReactNode;
  /** ⟦C31⟧ Le coût — ne s'affiche plus sous l'objet (même décision que `nom` ci-dessus, le
      bouton d'action du bas le porte déjà) : sert uniquement à compléter le nom accessible. */
  cout?: string;
  desactive?: boolean;
}

/** ⟦C31⟧ Le nom accessible d'un objet de la chambre : le nom, complété du coût quand il y en a
 *  un — la SEULE façon dont `nom`/`cout` atteignent encore le joueur, un lecteur d'écran ou le
 *  clavier (`aria-label`), depuis que la plaque visible (nom + coût) a quitté la scène
 *  (décision de Matt du 16/09, docs/chantiers/C31-sans-etiquettes.md : « pas besoin des
 *  étiquettes [...], vu que tu as déjà les boutons d'action en bas »). Une virgule, jamais un
 *  tiret : « Le lit, 8 h » se lit d'une traite par un lecteur d'écran. */
export function libelleAccessibleObjet(objet: Pick<ObjetChambre, "nom" | "cout">): string {
  return objet.cout ? `${objet.nom}, ${objet.cout}` : objet.nom;
}

/**
 * ⟦PLAN §4.13⟧ **Les améliorations cosmétiques de la chambre, palier par palier.**
 *
 * « Les récompenses de palier (on célèbre, on ne bloque pas) : +2 PC à chaque palier,
 * améliorations cosmétiques de la chambre — et surtout les plaques. » Les plaques étaient
 * livrées, le reste ne l'était pas : entre 100 et 1 M, seul le coin haut-droit changeait.
 *
 * Chaque palier franchi pose donc un objet **purement décoratif** dans le décor — il se
 * regarde, il ne se clique pas, et son infobulle raconte d'où il vient. La source est
 * `state.milestones_reached`, le même historique que les plaques : une chaîne qui redescend
 * ne perd pas ses souvenirs.
 *
 * ⟦C55 · A-172, décision de Matt du 01/10/2026⟧ **Chaque objet est un CALQUE pixel art posé DANS
 * le dessin de `chambre-n1`**, plus un emoji qui flotte (`calqueDecoChambreN1`, `game/chambre.ts`,
 * qui porte le fichier et le rectangle de chaque objet ; ce tableau ne garde que ce qui relève du
 * jeu : le palier et l'infobulle). Infobulles inchangées, mot pour mot. La chambre ne change plus
 * de planche à aucun palier (`niveauChambre`, toujours 1) : seuls ces objets la font grandir.
 *
 * ⟦A-10⟧ État neutre : aucun palier franchi ⇒ aucun décor, la chambre de départ est celle
 * d'avant, au pixel près.
 */
const DECORS_PALIER: readonly {
  palier: number;
  id: IdDecorPalier;
  titre: string;
}[] = [
  {
    palier: 100,
    id: "plante",
    titre: "La plante des 100 abonnés — la chaîne devient sérieuse, la chambre respire.",
  },
  {
    palier: 1_000,
    id: "fauteuil",
    titre: "Le fauteuil du Grand Saut — 1 000 abonnés, et un endroit où s'écrouler.",
  },
  {
    palier: 10_000,
    id: "fan-art",
    titre: "10 000 abonnés — le premier fan art, encadré et accroché au mur.",
  },
  {
    palier: 100_000,
    id: "lampe",
    titre: "100 000 abonnés — la lampe d'ambiance : le studio ressemble enfin à un studio.",
  },
  {
    palier: 500_000,
    id: "micro",
    titre: "500 000 abonnés — le micro de scène, pour les grandes occasions.",
  },
  {
    palier: 1_000_000,
    id: "mascotte",
    titre: "Le million — la mascotte géante offerte par la communauté.",
  },
];

/**
 * ⟦A-84 · A-4 décision 14 · SPEC-CONTENU §5bis.3⟧ **Le premier virement, encadré.** « L'encaisser
 * ou l'encadrer (argent perdu, bonus image, le cadre rejoint le décor de la chambre) » — la
 * moitié « décor » de la promesse. Le flag de contenu `virement_encadre` est posé par le choix
 * « L'imprimer et l'encadrer » de l'événement `premier_virement` ; c'est un cosmétique de plus,
 * dans la veine des décors de palier : il se regarde, il ne se clique pas, et son infobulle
 * raconte d'où il vient. Absent tant que le flag ne l'est pas — l'état neutre, au pixel près.
 * ⟦C55 · A-172⟧ Calque pixel art au mur de `chambre-n1`, comme les sept autres objets.
 */
const FLAG_CADRE_VIREMENT = "virement_encadre";
const CADRE_VIREMENT: { id: IdDecorPalier; titre: string } = {
  id: "virement",
  titre: "Ton premier virement MeTube, imprimé et encadré — des Clicks qui ne seront jamais dépensés.",
};

/**
 * ⟦C55 bis · A-175, décision de Matt du 02/10/2026⟧ **Le tableau d'honneur.** « Pour la chambre, les
 * médailles, ça doit être un tableau qui s'ajoute. » Les plaques (`state.milestones_reached` ≥
 * 100 000 : 100 k, 500 k, 1 M, puis 2 M, 5 M, 10 M après la rue) ne sont plus une rangée d'emojis
 * 🥈/🥇 posée HORS de la pièce, dans le bleu du fond (`.plaques-mur`, retirée) : un tableau dessiné,
 * calque pixel art du mur de `chambre-n1` comme les objets de palier, apparaît à la première plaque
 * et reste vide de plaque tant qu'on n'en a pas gagné d'autre ; chaque plaque s'y accroche ensuite
 * en calque dessiné, dans SA case (`calquePlaqueChambreN1`, `game/chambre.ts`). Le nombre entier
 * reste lisible au survol de chaque plaque, comme avant (`title`). Même règle d'état neutre que
 * tout le reste : aucune plaque ⇒ aucun tableau, la chambre d'avant au pixel près.
 */
const TITRE_TABLEAU = "Le tableau d'honneur : chaque plaque décrochée s'y accroche, une case par palier.";

/**
 * ⟦C21 bis · point 2⟧ Le décalage de l'ÉTIQUETTE d'un objet — jamais du hotspot de clic « sous »
 * l'étiquette, puisque pour le lit et le bureau (peints dans le fond, sans emoji propre)
 * l'étiquette EST le hotspot : la déplacer déplace le clic avec elle, et c'est très exactement
 * ce que demande la correction (« la plaque du lit est décalée pour ne pas toucher le sprite au
 * repos »). En % D'IMAGE, comme `HOTSPOTS_PAR_SCENE` — reste correct après
 * `positionDansBoite`, à n'importe quelle taille d'écran.
 *
 * Mesuré (pas deviné) : au repos, le rectangle de `.objet-lit` chevauchait celui de
 * `.avatar-chambre` de 2 800 à 4 600 px² selon la taille (390×844, 1280×800, 1920×1080) — le
 * lit (n1 : x 44, y 56, l'ANCIENNE planche) est trop proche du point de repos de l'avatar
 * (47, 68). Un décalage de −11 en y (vers le haut, loin du point de repos) ramène le
 * chevauchement à 0 px² aux trois tailles (`verif-point2.cjs`), sans toucher le bureau
 * (n1 : 63, 46, à 19 points d'écart en x).
 *
 * ⟦C26⟧ Sur une scène à planches d'état (`scenePossedePlanchesEtat`, aujourd'hui `chambre-n1`
 * seulement), l'avatar ne se superpose plus jamais au repos (il est peint dans la planche, ou
 * absent) : ce décalage n'a plus de raison d'être là, il déplacerait l'étiquette « Lit » sans
 * qu'aucun sprite ne menace de la recouvrir. `Chambre.tsx` ne le lit donc que pour les scènes
 * SANS planche d'état (n2 à n5) — la table elle-même reste inchangée, calibrée pour ces
 * scènes-là comme avant ce chantier.
 */
const DECALAGE_ETIQUETTE: Readonly<Record<string, { x?: number; y?: number }>> = {
  lit: { y: -11 },
};

/** ⟦C21 bis · point 1⟧ Le point de repos de l'avatar (`app.css`, `.avatar-chambre` : le défaut
 *  CSS `left:47%;top:68%`, gardé en CSS comme repli avant la première mesure) — repris ici pour
 *  passer par `positionDansBoite` comme n'importe quel hotspot : quel que soit le mode
 *  (`cover` recadre, `contain` ancre en haut, ⟦C21 ter⟧), un point « en % de l'image » doit
 *  toujours être reconverti en % de boîte. */
const REPOS_AVATAR = { x: 47, y: 68 };

/**
 * ⟦C21 bis · point 1⟧ Mesure la boîte RÉELLEMENT affichée de `.chambre-scene` (elle n'a plus de
 * ratio imposé : `background-size` la remplit, en `cover` ou en `contain` selon `ajustement`
 * ⟦C21 ter⟧, quel que soit son ratio, cf. le commentaire de `positionDansBoite`). Repli sur le
 * ratio natif de l'image tant que rien n'est mesuré (premier rendu) : ce repli équivaut à
 * l'identité, donc aucun hotspot ne saute au premier paint puis se déplace au montage, il est
 * seulement recalé une fois la vraie boîte connue.
 */
function useTailleBoiteScene(ref: { current: HTMLElement | null }) {
  const [taille, setTaille] = useState({ largeur: 1024, hauteur: 576 });
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observateur = new ResizeObserver((entrees) => {
      const rect = entrees[0]?.contentRect;
      if (rect && rect.width > 0 && rect.height > 0) {
        setTaille({ largeur: rect.width, hauteur: rect.height });
      }
    });
    observateur.observe(el);
    return () => observateur.disconnect();
  }, [ref]);
  return taille;
}

/**
 * ⟦C26 §2⟧ **Le fondu entre deux fonds de scène, jamais un flash.** Sert aussi bien au
 * changement de planche d'état (survol d'un objet, sommeil, travail) qu'au changement de
 * planche de base (jour/nuit, niveau) : un seul mécanisme, jamais un cas spécial pour l'un ou
 * l'autre. `affichee` reste à l'écran à opacité 1 pendant toute la transition ; `entrante`
 * (présente seulement le temps du fondu) se pose PAR-DESSUS avec l'animation CSS
 * `chambre-fondu-etat` (`app.css`, coupée par `prefers-reduced-motion`) ; à la fin du délai,
 * `affichee` prend la valeur cible et `entrante` redevient `null` — jamais deux couches qui
 * traînent indéfiniment.
 *
 * ⟦C29 · lot A · point 3⟧ **150 → 250 ms.** Le chantier demande un fondu « 200 à 300 ms » : 150
 * était en dessous de la fourchette (verbatim de Matt, 14/09 : « il y a bien le mouvement qui se
 * passe » attendu au survol). 250, au milieu de la fourchette, reste synchronisé avec la durée
 * de `chambre-fondu-etat` dans `app.css` (les deux DOIVENT s'accorder : ce minuteur démonte la
 * couche `entrante` au moment précis où son animation CSS termine, jamais avant ni après).
 *
 * ⟦C34 · A-159⟧ **Générique** (`T`, comparé par `===`) : sert au fond (une chaîne, comme avant),
 * mais aussi au calque de pose de `chambre-n1` (`PoseAffichee | null`, ⟦C40⟧ — « aucune pose
 * survolée » a bien besoin de fondre, pas seulement d'apparaître). `===` reste correct pour les
 * deux usages puisque `T` y est toujours une primitive (chaîne ou union de chaînes/`null`), jamais un objet
 * neuf à chaque rendu — un objet littéral casserait la comparaison d'identité et redéclencherait
 * le minuteur à chaque rendu, jamais ce que ce hook doit faire.
 */
function useFondScene<T>(cible: T, dureeMs = 250) {
  const [couches, setCouches] = useState<{ affichee: T; entrante: T | null }>({
    affichee: cible,
    entrante: null,
  });
  useEffect(() => {
    setCouches((c) => {
      // La cible redevient celle déjà affichée EN DESSOUS (ex. survol quitté avant la fin d'un
      // fondu en cours) : la couche entrante n'a plus de raison d'exister, elle est retirée tout
      // de suite plutôt que de rester bloquée à mi-fondu (le minuteur ci-dessous, programmé pour
      // l'ANCIENNE cible, ne la nettoierait jamais : sa condition `entrante === cible` ne
      // matcherait plus).
      if (cible === c.affichee) return c.entrante === null ? c : { affichee: c.affichee, entrante: null };
      return c.entrante === cible ? c : { affichee: c.affichee, entrante: cible };
    });
    const id = window.setTimeout(() => {
      setCouches((c) => (c.entrante === cible ? { affichee: cible, entrante: null } : c));
    }, dureeMs);
    return () => window.clearTimeout(id);
  }, [cible, dureeMs]);
  return couches;
}

export function Chambre({
  state,
  objets,
  pose = "idle",
  ajustement = "cover",
  basOccupeMobile = false,
  survolExterne = null,
}: {
  state: GameState;
  objets: readonly ObjetChambre[];
  /** ⟦C14 · A-133⟧ La pose du sprite héros — état neutre par défaut : `"idle"`, comme avant
      que `Studio.tsx` ne calcule les trois signaux de `game/heros.ts`. */
  pose?: PoseHeros;
  /** ⟦C21 ter · point 1⟧ Comment la planche remplit sa boîte : `"cover"` (ordinateur, défaut,
      inchangé depuis ⟦C21 bis⟧) recadre l'image pour ne jamais laisser de bande ; `"contain"`
      (mobile, `Studio.tsx` la passe explicitement) montre la pièce ENTIÈRE, ancrée en haut, le
      tour rempli par `couleurCoinChambre(scene)`. Piloté par le PARENT (deux instances de
      `<Chambre>` existent déjà, une par zone, jamais montées ensemble) plutôt que deviné ici
      via une media query : `Studio.tsx` sait déjà, par `matchMedia`, laquelle des deux est
      montée. ⟦C21 quater · point 2⟧ Un NOMBRE (ex. `1.35`) est un troisième mode : l'échelle
      EXPLICITE, cale sur la largeur × ce facteur (`rectangleImageAffichee`, `game/chambre.ts`),
      toujours ancrée en haut comme `"contain"`, mais volontairement plus grande que la boîte
      pour rogner les marges bleues de la planche plutôt que de laisser la pièce minuscule. */
  ajustement?: AjustementScene;
  /** ⟦C32 · A-157⟧ **Le survol externe** — l'id de l'objet que survole ou focus en ce moment un
      bouton d'action de `Studio.tsx` (Dormir, Publier un short, Veille…), en dehors de tout
      survol/focus DIRECT sur la scène. Demande de Matt du 16/09 : « je voudrais que les boutons
      d'action en bas changent aussi la position de l'avatar, comme au hover et clic sur
      l'image. » Combiné plus bas avec `hoverId` (le survol interne, posé par les hotspots de la
      scène eux-mêmes) : un seul et même mécanisme fait bouger l'avatar et basculer la planche
      d'état, que la source du survol soit l'image ou un bouton. `null`/absent (défaut) : état
      neutre, aucun changement de comportement pour un appelant qui ne le passe pas. */
  survolExterne?: string | null;
  /** ⟦C26 ter · point 1⟧ Mobile seulement (ignoré en `cover`) : quelque chose est-il POSÉ EN BAS
      de l'écran en ce moment (la fenêtre Actions ouverte par défaut, ⟦C21 §1.4⟧, ou la boîte de
      Matt) ? `Studio.tsx` le sait déjà (`ongletMobile !== null || matt !== null`, la même
      condition que celle qui choisit quoi monter sous la pièce), `Chambre.tsx` n'a qu'à en tirer
      l'ancrage : `true` place la pièce juste sous l'en-tête (ancrage `"haut"`, le vide restant
      tombe alors ENTRE la pièce et cet élément, jamais au-dessus) ; `false` (rien en bas, la
      fenêtre repliée) la recentre dans tout l'espace libre (ancrage `"centre"`, « la chambre
      respire »). Défaut `false` : sans lecture explicite du parent, la pièce respire plutôt que
      de coller à un bord qui n'existe pas. */
  basOccupeMobile?: boolean;
}) {
  const plaques = state.milestones_reached.filter((p) => p >= 100_000);
  // Le décor gagné : l'historique des paliers, jamais le compteur d'abonnés du moment.
  const paliersFranchis = state.milestones_reached as readonly number[];
  // ⟦C15 · A-135 · A-136⟧ Le fond de scène a CINQ niveaux (`game/chambre.ts`) au lieu des deux
  // R1/R2 du reskin 8-bit (⟦C14 · A-134⟧) — même principe : dérivé de l'historique de paliers,
  // aucun nouveau flag. La variante nuit suit l'horloge simulée, pas un mode inventé.
  const niveau = niveauChambre(paliersFranchis);
  const nuit = estNuit(state.clock_h);
  const scene = nomScene(niveau, nuit);
  // ⟦C55 · A-172⟧ Les objets de palier gagnés, dans l'ordre des paliers, puis le cadre du premier
  // virement (un flag de contenu, pas un palier) : chacun devient un calque de `chambre-n1`.
  const decors = DECORS_PALIER.filter((d) => paliersFranchis.includes(d.palier));
  const cadreVirement = hasContentFlag(state, FLAG_CADRE_VIREMENT);
  const decorsPoses: readonly { id: IdDecorPalier; titre: string }[] = cadreVirement ? [...decors, CADRE_VIREMENT] : decors;
  const zoneMental = zoneSM(state.mental);

  // ⟦C21 bis · point 1⟧ La boîte réellement affichée (mesurée, pas devinée) — sert à reconvertir
  // toute position relevée en % DE L'IMAGE en % DE LA BOÎTE, quel que soit le mode
  // (`positionDansBoite`, `game/chambre.ts`) : `cover` (ordinateur) recadre, `contain` (mobile,
  // ⟦C21 ter⟧) ancre en haut sans recadrer.
  const sceneRef = useRef<HTMLDivElement>(null);
  const boite = useTailleBoiteScene(sceneRef);
  // ⟦C21 quater · point 2⟧ `ajustement="contain"` reste la demande du PARENT (« montre la pièce
  // entière, mobile ») ; Chambre.tsx choisit COMMENT planche par planche : `echellePieceMobile`
  // remplace le `Math.min` neutre de `contain` par un agrandissement calé sur la marge réelle de
  // CETTE scène (mesurée, jamais un seul facteur pour les cinq : n1 a besoin d'un vrai zoom,
  // n5 remplit déjà le cadre bord à bord et n'en veut presque aucun, voir `game/chambre.ts`).
  // `"cover"` (ordinateur) ne passe jamais par ce chemin, aucun changement de ce côté.
  const ajustementEffectif: AjustementScene = ajustement === "contain" ? echellePieceMobile(scene) : ajustement;
  // ⟦C26 ter · point 1⟧ Mobile (`ajustement !== "cover"`) : l'ancrage BAS (⟦C26 bis · point 3⟧)
  // remontait TOUT le vide résiduel entre l'en-tête et la pièce, un demi-écran de bleu vide dès
  // que la fenêtre Actions par défaut (⟦C21 §1.4⟧) ne suffisait plus à le remplir (constat du
  // chantier). La pièce s'ancre désormais en HAUT quand quelque chose occupe le bas de l'écran
  // (`basOccupeMobile`, la fenêtre ou la boîte de Matt) : le vide restant, s'il y en a, tombe
  // alors ENTRE la pièce et cet élément, jamais au-dessus. Rien n'occupe le bas (fenêtre
  // repliée) : la pièce se RECENTRE dans tout l'espace libre (`"centre"`, `game/chambre.ts`),
  // « la chambre respire » (⟦C21 §4⟧). `"bas"` reste un ancrage valide de `positionDansBoite`
  // (générique, testé), mais cet écran ne le demande plus jamais. Un seul point de vérité, lu
  // par TOUT ce qui convertit une position en % d'image vers un % de boîte ci-dessous (hotspots,
  // avatar, effets, ancre de plaque, fond CSS) : jamais un calcul qui déciderait pour lui-même.
  const ancrageVertical: AncrageVertical =
    ajustement !== "cover" && !basOccupeMobile ? "centre" : "haut";
  // ⟦C21 quater · point 1⟧ Le rectangle RÉELLEMENT affiché de l'image (même calcul que
  // `positionDansBoite` utilise en interne, factorisé dans `rectangleImageAffichee`) : sert à
  // caler la TAILLE de l'avatar dessus (`--chambre-image-h` plus bas), pas seulement la
  // position des hotspots : en mode `contain`/échelle explicite, la boîte peut être bien plus
  // haute que l'image (le tour est comblé par `couleurCoinChambre`), et un pourcentage de la
  // hauteur de BOÎTE surdimensionne l'avatar (constat du chantier : « le personnage fait deux
  // fois la chambre »).
  const rectImage = rectangleImageAffichee(boite, ajustementEffectif);

  // ⟦C16 §1⟧ **Le calque avatar suit l'objet survolé** — trois calques, comme demandé : le
  // fond (au-dessus), le calque objet (le halo au survol, `app.css`), et ce calque-ci. Verbatim
  // de Mathieu : « les boutons sont trop décorrélés du fond » — l'avatar qui se pose SUR
  // l'objet visé recolle les deux.
  //
  // `hoverId` ne mémorise QUE l'id survolé/focus : la position et la pose sont recalculées à
  // chaque rendu à partir de LUI et de `hotspotChambre(scene, hoverId)` — jamais une coordonnée
  // gardée en état, pour ne jamais désynchroniser du relevé par planche de `game/chambre.ts`
  // (⟦A-137⟧, « les hotspots suivent le dessin, jamais l'inverse »).
  const [hoverId, setHoverId] = useState<string | null>(null);
  // ⟦C32 · A-157⟧ **Le survol effectif** — `survolExterne` (un bouton d'action de `Studio.tsx`)
  // prime sur `hoverId` (les hotspots de la scène) : les deux ne sont normalement jamais actifs
  // en même temps (un bouton et un hotspot ne se survolent pas ensemble), et tout ce qui suit
  // (déplacement de l'avatar, planche d'état, fondu) lit désormais CETTE valeur combinée,
  // jamais `hoverId` seul — exactement le même mécanisme, quelle que soit la source du survol.
  const hoverEffectif = survolExterne ?? hoverId;
  // ⟦C30 · lot E · point 3⟧ **Les étiquettes ne se posent plus sur le texte de Matt.** Depuis
  // ⟦C29 · lot A⟧ les zones et leurs étiquettes passent AU-DESSUS de la boîte de Matt (z-index 9
  // et 10 > 6/8, `app.css`) — validé par Matt, gardé tel quel. Le défaut : rien n'empêchait
  // l'étiquette de se dessiner PAR-DESSUS le texte de la réplique quand son ancre (souvent basse
  // dans l'image, ⟦C26 bis · point 1⟧) tombe géométriquement derrière la boîte. Mesuré au
  // rendu (`getBoundingClientRect`, jamais une zone devinée à l'avance : la boîte n'a pas de
  // hauteur fixe, 1 à 3 lignes selon la réplique) plutôt que calculé : à chaque peinture, si le
  // rectangle d'un objet chevauche celui de `.boite-corps` (texte ET portraits, les deux exclus
  // par le chantier), il se décale VERS LE HAUT de tout ce qu'il faut pour dégager le bord haut de
  // la boîte, plus une petite marge — jamais vers le bas, jamais latéralement (la boîte occupe
  // toujours toute la largeur du bas de la scène, un décalage horizontal ne dégagerait rien). Un
  // objet qui ne chevauche personne (la boîte est fermée, ou son ancre est haute dans l'image)
  // n'a aucun décalage : état neutre par défaut, aucun changement pour ce que Matt a validé.
  const [decalagesBoite, setDecalagesBoite] = useState<Readonly<Record<string, number>>>({});
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    let idAnimation: number;
    const mesurer = () => {
      const boiteCorps = document.querySelector<HTMLElement>(".zone-boite .boite-corps");
      const suivants: Record<string, number> = {};
      if (boiteCorps) {
        const boiteRect = boiteCorps.getBoundingClientRect();
        scene.querySelectorAll<HTMLElement>("[data-objet]:not(.objet-hotspot-etat)").forEach((el) => {
          const id = el.dataset.objet;
          if (!id) return;
          const r = el.getBoundingClientRect();
          const necessaire = decalageAntiChevauchementBoite(r, boiteRect, decalagesBoite[id] ?? 0);
          if (necessaire > 0) {
            suivants[id] = Math.max(suivants[id] ?? 0, necessaire);
          }
        });
      }
      setDecalagesBoite((precedent) => {
        const cles = Object.keys(suivants);
        const inchange =
          cles.length === Object.keys(precedent).length && cles.every((k) => Math.abs((precedent[k] ?? 0) - suivants[k]!) < 0.5);
        return inchange ? precedent : suivants;
      });
    };
    // Une frame de battement : les rectangles ci-dessus doivent lire la mise en page APRÈS que
    // React a posé les positions calculées plus haut dans ce rendu, jamais celles du rendu
    // précédent (mesurer pendant l'effet, avant paint, verrait encore l'ancien DOM).
    idAnimation = window.requestAnimationFrame(mesurer);
    return () => window.cancelAnimationFrame(idAnimation);
  });
  /** Le décalage vertical (px, vers le haut) à soustraire au `top` d'un objet pour dégager le
   *  texte/les portraits de la boîte de Matt — 0 si aucun chevauchement mesuré. */
  const decalageBoitePct = (id: string): number => {
    const px = decalagesBoite[id];
    if (!px || boite.hauteur <= 0) return 0;
    return (px / boite.hauteur) * 100;
  };
  // ⟦C23 §3⟧ Retour de Matt (la 3e fois) : « le personnage qui va au bureau ne matche pas
  // [dans le bureau]. » Bureau et lit continuent de faire SUIVRE l'avatar (`estObjetAvecSuivi`,
  // `game/heros.ts`) : seule la fenêtre change encore sa POSTURE (`spriteHeroSurvol`) : le
  // déplacement et la posture sont désormais deux choses distinctes, là où avant ce chantier
  // `spriteSurvol !== null` gouvernait les deux à la fois.
  const suit = hoverEffectif !== null && estObjetAvecSuivi(hoverEffectif);
  const positionSurvol = suit ? hotspotChambre(scene, hoverEffectif!).position : null;
  // `spriteHeroSurvol` ne renvoie plus qu'un chemin pour la fenêtre : bureau/lit valent `null`
  // ici (l'avatar s'y déplace mais reste `idle`), exactement comme un objet sans pose dédiée.
  const spriteSurvol = positionSurvol ? spriteHeroSurvol(hoverEffectif!, state.genre) : null;
  const avatarDeplace = positionSurvol !== null;
  // ⟦C21 bis · point 1⟧ Repos OU survol, toujours reconverti en % de boîte : avant ce chantier,
  // le repos restait un `left`/`top` FIXE posé en CSS (`.avatar-chambre`) parce que la boîte
  // avait forcément le ratio de l'image — ce n'est plus vrai (`background-size: cover`), donc le
  // repos passe désormais par la même conversion que le survol, en JS, pour rester au même
  // endroit du DESSIN plutôt que de la boîte. ⟦C23 §3⟧ Le garde n'est plus `positionSurvol &&
  // spriteSurvol` (bureau/lit n'ont plus de sprite dédié) mais `positionSurvol` seul : c'est
  // `avatarDeplace` qui porte désormais la seule condition qui compte, le déplacement.
  const positionAvatar = positionDansBoite(positionSurvol ?? REPOS_AVATAR, boite, ajustementEffectif, ancrageVertical);
  // ⟦C23 §3⟧ **Le personnage qui dort/travaille : plus de sprite couché/assis, il entre dans le
  // meuble.** L'avatar debout DISPARAÎT (fondu court, `.avatar-chambre-cachee`) et l'objet prend
  // vie à sa place (bulle « z z z » sur le lit, écran allumé + bulle « ▶ REC » sur le bureau,
  // plus bas). Ne vaut que pour `sommeil`/`travail` : `victoire` (la célébration de palier)
  // garde son sprite R3 dédié, inchangé : seules les deux postures qui ne matchaient aucune
  // planche de fond sont concernées.
  const poseCachee = pose === "sommeil" || pose === "travail";
  const litObjet = objets.find((o) => o.id === "lit");
  const bureauObjet = objets.find((o) => o.id === "bureau");
  const positionLitEffet =
    poseCachee && pose === "sommeil" && litObjet
      ? positionDansBoite(
          hotspotChambre(scene, "lit").position ?? { x: litObjet.x, y: litObjet.y },
          boite,
          ajustementEffectif,
          ancrageVertical,
        )
      : null;
  const positionBureauEffet =
    poseCachee && pose === "travail" && bureauObjet
      ? positionDansBoite(
          hotspotChambre(scene, "bureau").position ?? { x: bureauObjet.x, y: bureauObjet.y },
          boite,
          ajustementEffectif,
          ancrageVertical,
        )
      : null;
  const positionEcranEffet = (() => {
    if (!(poseCachee && pose === "travail")) return null;
    const p = positionEcranBureau(scene);
    return p ? positionDansBoite(p, boite, ajustementEffectif, ancrageVertical) : null;
  })();

  // ⟦C34 · A-159, décision de Matt du 17/09⟧ **`chambre-n1` en calques.** « Quand je vais sur le
  // lit, il n'y a vraiment que la zone du lit qui change [...] au bureau, il y a le cadre de la
  // trend qui bouge [...] le panneau de trend, au départ, ce soit des cadres photo. » Le défaut
  // (⟦C26 · A-150⟧, ⟦C33 · A-158⟧) : chaque planche d'état était une image ENTIÈRE régénérée par
  // situation, donc le mur n'y était jamais strictement identique d'une planche à l'autre —
  // survoler un meuble « bougeait » le mur, même sans le toucher.
  //
  // `estSceneCalques` (seule `chambre-n1` aujourd'hui, `scenePossedePlanchesEtat`) choisit
  // désormais entre deux rendus complets : le fond FIXE + calques ci-dessous, ou le fond unique +
  // sprite superposé de n2-n5 (comportement inchangé pour ces niveaux-là).
  const estSceneCalques = scenePossedePlanchesEtat(scene);

  // ⟦C26 · A-150⟧ **La pose d'état à afficher, si elle existe.** Le survol d'un objet qui a sa
  // propre pose d'état (`lit`/`bureau`/`fenetre`/`tableau`, mêmes chaînes que les ids d'objet,
  // `estPoseEtat`) prime sur la pose de JEU — « le survol montre la planche de l'objet, c'est
  // l'aperçu » — sinon la pose de jeu courante (`idle`→`debout`, `sommeil`→`lit`,
  // `travail`→`bureau`, `victoire`→ aucune planche, elle garde son sprite R3).
  // ⟦C33 · A-158 · C40 · A-163 · C41 · A-164⟧ `poseEtatDeObjet` (jamais `estPoseEtat(hoverEffectif)
  // ? hoverEffectif : null` seul) : le tableau des tendances n'a pas de planche peinte PARMI LES
  // SEPT (`estPoseEtat`), mais depuis ⟦C41⟧ elle a ses PROPRES calques (planche dédiée générée,
  // calée et découpée par `scripts/chambre-pose-tendances-c41.py` — voir le commentaire de
  // `PoseAffichee`, `game/chambre.ts`) : `poseEtatDeObjet("tendances")` s'y réduit maintenant à
  // l'identité, comme tout objet qui a bien sa propre pose (id ∈ `POSES_ETAT`).
  const poseEtatSurvol = hoverEffectif !== null ? poseEtatDeObjet(hoverEffectif) : null;
  const poseEtatActuelle = poseEtatSurvol ?? poseEtatDeBase(pose);

  // ⟦C34⟧ Le FOND ne change plus jamais avec la pose sur une scène en calques : c'est le calque
  // de pose (plus bas) qui porte désormais le personnage. `fondChambreN1` retombe sur le fond
  // sans genre pour les parties d'avant ⟦A-4 §2⟧ (et `FondMenu.tsx`, qui n'a pas de genre) — le
  // sprite CSS reprend alors le relais (`avatarSuperpose`, plus bas), comportement inchangé.
  const fondCible = estSceneCalques ? fondChambreN1(state.genre) : sceneChambre(niveau, nuit);
  const fond = useFondScene(fondCible);

  // ⟦C34 · C40 · A-163⟧ **Les calques de mur, gatés exactement comme les objets qu'ils
  // accompagnent** (`Studio.tsx`) : le tableau des tendances sur `ouvert(state, "tendances")`
  // (même condition que l'objet `tendances`/« Le tableau des tendances »). Aucun fondu demandé
  // pour lui (le chantier ne le demande que pour le calque de pose, plus bas) : il apparaît
  // directement au déblocage, un événement rare et déjà célébré ailleurs (jalon).
  //
  // Le liège, lui, ne pose plus TOUS ses post-it d'un coup (⟦C34⟧) mais un par compétence prise
  // (⟦C40⟧, retour de Matt du 18/09 : « ça se complète au fur et à mesure que tu prends des
  // compétences ») — `calquesLiegeChambreN1` renvoie un TABLEAU (zéro à N post-it), jamais un
  // objet unique, plafonné par elle-même au nombre de post-it disponibles pour ce genre.
  const calquesLiege = estSceneCalques && ouvert(state, "arbre") ? calquesLiegeChambreN1(state.genre, state.skills.length) : [];
  const calqueTendances = estSceneCalques && ouvert(state, "tendances") ? calqueTendancesChambreN1(state.genre) : null;

  // ⟦C34⟧ **Le calque de pose** (le personnage + son meuble, l'ancien contenu de la planche
  // d'état) : fondu de 250 ms à l'apparition ET à la disparition, comme demandé — même mécanisme
  // que `useFondScene` pour le fond, généralisé à `PoseAffichee | null` (« aucune pose » doit,
  // elle aussi, fondre ; ⟦C40⟧ élargit `PoseEtat` à `PoseAffichee` pour `"tendances"`, voir
  // `game/chambre.ts`). `null` pour `debout` (le fond EST cette pose, rien à superposer) et pour
  // tout ce qui n'est pas une scène en calques (n2-n5, comportement inchangé — sprite + planche
  // uniques). La clé passée au hook est la POSE seule (une primitive stable), jamais l'objet
  // `{chemin, rect}` de `calquePoseChambreN1` (reconstruit à chaque rendu, sa comparaison par
  // identité casserait le fondu) — chaque calque affiché/entrant résout son propre `{chemin,
  // rect}` au moment du rendu, plus bas.
  const poseCalqueCible: PoseAffichee | null =
    estSceneCalques && poseEtatActuelle && poseEtatActuelle !== "debout" ? poseEtatActuelle : null;
  const couchePose = useFondScene<PoseAffichee | null>(poseCalqueCible);

  // ⟦C26 · A-150 · C34⟧ Le sprite CSS (avatar superposé) et les effets d'écran/REC ne se montent
  // QUE quand aucun calque de pose ne peut couvrir la situation — genre inconnu (parties d'avant
  // ⟦A-4 §2⟧) sur une scène en calques, ou une scène qui n'en a pas du tout (n2-n5, comportement
  // inchangé). Sur une scène en calques avec un genre connu, le personnage est TOUJOURS porté par
  // le fond (repos) ou par le calque de pose : jamais les deux à la fois.
  const avatarSuperpose = !(estSceneCalques && state.genre);

  // ⟦C26 §3⟧ Les calques DU GENRE JOUÉ, préchargés à l'entrée en chambre — jamais ceux de
  // l'autre genre (aucune raison de les charger, `state.genre` ne change pas en cours de run).
  // `new Image()` plutôt qu'un `<link rel="preload">` : la liste dépend de la scène (seule
  // `chambre-n1` en a aujourd'hui) et du genre, tous deux connus seulement ici, côté composant.
  // ⟦C34⟧ Le calque tendances est toujours préchargé (il peut se débloquer en cours de run,
  // jamais un flash de téléchargement au déblocage), le calque de pose pour chacune des poses
  // atteignables (`POSES_ETAT`, `calquePoseChambreN1` renvoie `null` pour `debout`/`commode`,
  // rien à précharger pour ces deux-là). ⟦C40⟧ Le liège précharge désormais TOUS ses post-it
  // individuels (le nombre affiché grandit en cours de run à chaque compétence prise, jamais un
  // flash au moment où le Nᵉ post-it apparaît) — `calquesLiegeChambreN1` au nombre MAXIMUM
  // disponible pour ce genre couvre tous les N atteignables. ⟦C40 bis⟧ `calquePoseChambreN1`
  // renvoie désormais DEUX calques (`effacement`/`personnage`, `game/chambre.ts`) : les deux sont
  // préchargés. ⟦C41 · A-164⟧ `"tendances"` a maintenant ses PROPRES fichiers (planche dédiée,
  // `poseEtatDeObjet` s'y réduit à l'identité) : `POSES_ETAT` seul ne la couvre pas (ce n'est pas
  // une des sept planches peintes), elle rejoint donc explicitement la boucle ci-dessous.
  useEffect(() => {
    const genre = state.genre;
    // ⟦C55 · A-172⟧ Les sept objets de palier se préchargent aussi (ils apparaissent en cours de
    // run, au franchissement d'un palier : jamais un flash de téléchargement), genre inconnu
    // compris (leur fond « sans genre » a ses propres fichiers).
    if (estSceneCalques) {
      for (const id of IDS_DECOR_PALIER) new Image().src = calqueDecoChambreN1(id, genre).chemin;
    }
    if (!genre || !estSceneCalques) return;
    new Image().src = calqueTendancesChambreN1(genre).chemin;
    for (const postit of calquesLiegeChambreN1(genre, nombrePostItsDisponibles(genre))) {
      new Image().src = postit.chemin;
    }
    const posesAPrecharger: readonly PoseAffichee[] = [...POSES_ETAT, "tendances"];
    for (const p of posesAPrecharger) {
      const calques = calquePoseChambreN1(genre, p);
      if (calques?.effacement) new Image().src = calques.effacement.chemin;
      if (calques?.personnage) new Image().src = calques.personnage.chemin;
    }
  }, [scene, state.genre, estSceneCalques]);
  const styleFond = {
    backgroundColor: ajustement === "contain" ? couleurCoinChambre(scene) : undefined,
    backgroundSize: typeof ajustementEffectif === "number" ? `${ajustementEffectif * 100}%` : ajustementEffectif,
    // ⟦C26 ter · point 1⟧ Le mot-clé vertical de `background-position` suit `ancrageVertical` :
    // le fond CSS (peint ici) et les positions calculées en JS (hotspots, avatar, ancre de
    // plaque) doivent toujours s'accorder sur le MÊME bord, sous peine de hotspots qui ne
    // pointent plus sur le dessin réellement affiché. Cet écran ne calcule plus jamais que
    // `"haut"` (quelque chose occupe le bas de l'écran) ou `"centre"` (rien en bas, la pièce
    // respire, ⟦C26 bis · point 3⟧ supersédé) : `top`/`center`, respectivement.
    backgroundPosition: ajustement === "contain" ? `center ${ancrageVertical === "centre" ? "center" : "top"}` : "center",
  };

  // ⟦C34 · A-159⟧ Le style CSS d'un calque de `chambre-n1` (mur ou pose) : son rectangle
  // (`EmpriseObjet`, en % de l'image) reconverti par `styleEmpriseHotspot` — le MÊME calcul que
  // les hotspots (`positionDansBoite`/`rectangleImageAffichee`, `game/chambre.ts`), donc toujours
  // aligné au dessin réellement affiché, quel que soit le recadrage. `left`/`top` centrent
  // l'élément (`transform: translate(-50%, -50%)`, `.chambre-scene-calque`, `app.css`, le même
  // patron que `.objet`), `width`/`height` en px lui donnent sa taille RÉELLE à l'écran — jamais
  // une image étirée au hasard, juste mise à l'échelle comme le reste de la pièce.
  const styleCalque = (rect: EmpriseObjet) => {
    const r = styleEmpriseHotspot(rect, boite, ajustementEffectif, ancrageVertical);
    return { left: `${r.left}%`, top: `${r.top}%`, width: `${r.largeurPx}px`, height: `${r.hauteurPx}px` };
  };

  return (
    <div
      ref={sceneRef}
      className={`chambre-scene chambre-paliers-${decors.length}`}
      data-scene={scene}
      data-pose={pose}
      // ⟦C21 ter · point 1⟧ `background-size`/`background-position` suivent `ajustement` :
      // `cover` (ordinateur) centré, plein cadre, recadré ; `contain` (mobile) ancré en haut,
      // pièce entière visible. ⟦C26⟧ Le fond lui-même (`backgroundImage`) n'est plus posé ICI :
      // il vit dans les deux couches `.chambre-scene-fond` ci-dessous (`useFondScene`), pour le
      // fondu de 250 ms entre planches (⟦C29 · lot A · point 3⟧) : `backgroundColor` (couleur de
      // coin) et `background-size`/`-position` restent partagés entre les deux couches
      // (`styleFond`), donc listés une seule fois plus haut plutôt que dupliqués.
      style={{
        // ⟦C21 quater · point 1⟧ La hauteur RÉELLEMENT affichée de l'image, en pixels, lue par
        // `.avatar-chambre` (app.css) pour caler `--taille-heros` dessus plutôt que sur la
        // hauteur de cette boîte (héritée par tout descendant, la seule couche qui la lit
        // aujourd'hui). En mode `cover`, l'image remplit toujours 100 % de la boîte : cette
        // variable vaut alors la hauteur de la boîte elle-même, aucun changement de comportement
        // sur ordinateur.
        ["--chambre-image-h" as string]: `${rectImage.hauteur}px`,
      }}
      role="group"
      aria-label="Ta chambre"
    >
      {/* ⟦C26 §2⟧ Le fond de la scène, en deux couches pour le fondu de 250 ms (`useFondScene`, ⟦C29 · lot A · point 3⟧) :
          `affichee` reste posée pendant toute la transition, `entrante` (planche cible) se pose
          par-dessus et anime son opacité 0→1 (`app.css`, `.chambre-scene-fond-entrante`, coupée
          par `prefers-reduced-motion`). Jamais de flash : l'ancienne planche reste visible sous
          la nouvelle tant que le fondu n'est pas terminé. */}
      <div
        className="chambre-scene-fond"
        style={{ backgroundImage: `url(${fond.affichee})`, ...styleFond }}
        data-fond={fond.affichee}
      />
      {fond.entrante ? (
        <div
          key={fond.entrante}
          className="chambre-scene-fond chambre-scene-fond-entrante"
          style={{ backgroundImage: `url(${fond.entrante})`, ...styleFond }}
          data-fond={fond.entrante}
        />
      ) : null}

      {/* ⟦C34 · A-159 · C40 · A-163⟧ **Les calques de `chambre-n1`**, posés sur le fond fixe —
          chacun rogné à sa propre boîte englobante (`game/chambre.ts`), donc géométriquement
          incapable de dessiner en dehors : le mur (post-it individuels du liège, tableau des
          tendances, gatés exactement comme les objets qu'ils accompagnent, `Studio.tsx`) sans
          fondu, puis le calque de pose (le personnage + son meuble) EN DERNIER — au-dessus des
          calques de mur, pour que le personnage occulte bien le mur quand il s'y tient devant (la
          pose `tableau`, la main sur un post-it) — avec le même fondu de 250 ms que le fond
          (`useFondScene`, `.chambre-scene-calque-entrante`, `app.css`). Chaque rectangle est
          reconverti par `styleEmpriseHotspot`, le MÊME calcul que les hotspots : aligné au dessin
          quel que soit le recadrage (`cover`/`contain`/échelle mobile), jamais un second calcul
          divergent.

          ⟦C40⟧ Le liège n'est plus JAMAIS posé d'un bloc : `calquesLiege` (plus haut) est un
          TABLEAU, un `<div>` par post-it réellement gagné (compétence prise), zéro élément si
          aucune compétence n'est prise (le fond montre alors le liège nu tel quel). */}
      {calquesLiege.map((postit) => (
        <div
          key={postit.chemin}
          className="chambre-scene-calque"
          style={{
            backgroundImage: `url(${postit.chemin})`,
            ...styleCalque(postit.rect),
          }}
          aria-hidden="true"
        />
      ))}
      {calqueTendances ? (
        <div
          className="chambre-scene-calque"
          style={{
            backgroundImage: `url(${calqueTendances.chemin})`,
            ...styleCalque(calqueTendances.rect),
          }}
          aria-hidden="true"
        />
      ) : null}
      {(() => {
        // ⟦C40 bis · A-163⟧ Chaque pose est désormais DEUX calques (`effacement`/`personnage`,
        // `calquePoseChambreN1`, `game/chambre.ts`) — jamais un seul `<div>` par couche du fondu,
        // deux. `effacement` d'abord (repeint la zone du repos), `personnage` ensuite PAR-DESSUS
        // (occulte le mur/liège quand il s'y tient devant, comportement ⟦C34⟧ inchangé) : le même
        // ordre que `composer()` dans `scripts/chambre-calques-effacement-c40bis.py`.
        const calqueAffiche = couchePose.affichee ? calquePoseChambreN1(state.genre, couchePose.affichee) : null;
        const calqueEntrant = couchePose.entrante ? calquePoseChambreN1(state.genre, couchePose.entrante) : null;
        return (
          <>
            {calqueAffiche?.effacement ? (
              <div
                className="chambre-scene-calque"
                style={{
                  backgroundImage: `url(${calqueAffiche.effacement.chemin})`,
                  ...styleCalque(calqueAffiche.effacement.rect),
                }}
                data-calque-pose={`${couchePose.affichee}-effacement`}
                aria-hidden="true"
              />
            ) : null}
            {calqueAffiche?.personnage ? (
              <div
                className="chambre-scene-calque"
                style={{
                  backgroundImage: `url(${calqueAffiche.personnage.chemin})`,
                  ...styleCalque(calqueAffiche.personnage.rect),
                }}
                data-calque-pose={`${couchePose.affichee}-personnage`}
                aria-hidden="true"
              />
            ) : null}
            {calqueEntrant?.effacement ? (
              <div
                key={`${couchePose.entrante}-effacement`}
                className="chambre-scene-calque chambre-scene-calque-entrante"
                style={{
                  backgroundImage: `url(${calqueEntrant.effacement.chemin})`,
                  ...styleCalque(calqueEntrant.effacement.rect),
                }}
                data-calque-pose={`${couchePose.entrante}-effacement`}
                aria-hidden="true"
              />
            ) : null}
            {calqueEntrant?.personnage ? (
              <div
                key={`${couchePose.entrante}-personnage`}
                className="chambre-scene-calque chambre-scene-calque-entrante"
                style={{
                  backgroundImage: `url(${calqueEntrant.personnage.chemin})`,
                  ...styleCalque(calqueEntrant.personnage.rect),
                }}
                data-calque-pose={`${couchePose.entrante}-personnage`}
                aria-hidden="true"
              />
            ) : null}
          </>
        );
      })()}

      {/* ⟦C55 · A-172⟧ **Les objets de palier, calques pixel art posés DANS le dessin de n1** (la
          plante, le fauteuil, le fan art, la lampe, le micro, la mascotte, le virement encadré) :
          chacun rogné à sa boîte englobante, converti par `styleCalque` comme les calques de mur
          et de pose (aligné au dessin quel que soit le recadrage), posé AU-DESSUS
          des calques de pose (donc jamais masqué par un `effacement`, qui repeint le décor
          d'origine, sans eux) mais jamais par-dessus le personnage : aucun calque de palier ne
          recouvre l'avatar au repos ni un calque `personnage`, mesuré par
          `scripts/chambre-calques-paliers-c55.py` pour f ET h. Un objet qui apparaît
          ne déplace rien d'autre (chaque calque ne dessine que dans sa propre boîte), et aucune
          boîte ne touche un meuble cliquable ni une zone de pose (`chambre.test.ts`). L'infobulle
          (`title`) vit sur le calque lui-même (`.chambre-scene-calque-deco` rend le survol au
          calque, `app.css`) : sa surface de survol EST l'emprise du dessin. Décoratifs : aucun
          clic, aucun rôle de bouton, `aria-label` seulement (lisible au lecteur d'écran). Sur une
          scène sans calques (jamais, depuis ⟦A-172⟧, `chambre-n1` seule), rien ne se pose. */}
      {estSceneCalques
        ? decorsPoses.map((deco) => {
            const calque = calqueDecoChambreN1(deco.id, state.genre);
            return (
              <div
                key={deco.id}
                className="chambre-scene-calque chambre-scene-calque-deco"
                style={{
                  backgroundImage: `url(${calque.chemin})`,
                  ...styleCalque(calque.rect),
                }}
                data-deco={deco.id}
                title={deco.titre}
                aria-label={deco.titre}
                role="img"
              />
            );
          })
        : null}

      {/* ⟦C55 bis · A-175⟧ **Le tableau d'honneur et ses plaques**, calques pixel art posés DANS le
          dessin de n1 comme les objets de palier ci-dessus (même `styleCalque`, même survol rendu au
          calque). Le tableau d'abord, puis les plaques DANS L'ORDRE DU DOM : à z-index égal, la
          dernière peinte est au-dessus, donc chaque plaque recouvre sa case. Une plaque qui s'ajoute
          ne déplace rien (chaque calque ne dessine que dans sa boîte), et son infobulle est son
          nombre entier, comme l'ancienne rangée. Un palier sans case (`calquePlaqueChambreN1`
          rend `null`, jamais aujourd'hui : `chambre.test.ts` compare les cases aux paliers du
          moteur) ne pose rien : ni emoji ni texte. */}
      {estSceneCalques && plaques.length > 0 ? (
        <>
          <div
            key="tableau"
            className="chambre-scene-calque chambre-scene-calque-deco"
            style={{
              backgroundImage: `url(${calqueDecoChambreN1("tableau", state.genre).chemin})`,
              ...styleCalque(calqueDecoChambreN1("tableau", state.genre).rect),
            }}
            data-deco="tableau"
            title={TITRE_TABLEAU}
            aria-label={TITRE_TABLEAU}
            role="img"
          />
          {plaques.map((p) => {
            const calque = calquePlaqueChambreN1(p, state.genre);
            if (!calque) return null;
            const titre = `${nombre(p)} abonnés`;
            return (
              <div
                key={p}
                className="chambre-scene-calque chambre-scene-calque-deco"
                style={{
                  backgroundImage: `url(${calque.chemin})`,
                  ...styleCalque(calque.rect),
                }}
                data-plaque={p}
                title={titre}
                aria-label={titre}
                role="img"
              />
            );
          })}
        </>
      ) : null}

      {/* ⟦C26 bis · point 3 bis⟧ **La teinte de nuit pendant le sommeil.** Un voile bleu nuit
          léger, en fondu, posé PAR-DESSUS le fond (peint ou doodle) tant que `pose === "sommeil"`,
          c'est-à-dire pendant toute la tenue de la planche « au lit » (`Studio.tsx`,
          `dormir()`), jamais avant ni après : état neutre par défaut, absent dès qu'on ne dort
          pas. `prefers-reduced-motion` : apparaît directement à pleine opacité (`app.css`), sans
          fondu, comme le reste de l'app. */}
      {pose === "sommeil" ? <div className="chambre-voile-nuit" aria-hidden="true" /> : null}

      {/* Le mur, la plinthe et le plancher : trois couches, zéro image. */}
      <div className="mur" />
      <div className="plancher" />

      {objets.map((objet) => {
        // ⟦Consigne de Mathieu, 29/08⟧ Les hotspots suivent le DESSIN de la scène courante,
        // jamais une position unique recopiée d'une planche à l'autre (`game/chambre.ts`
        // documente le relevé, meuble par meuble, planche par planche). `position: null` ⇒
        // cette scène ne dessine pas cet objet : on garde le défaut de Studio.tsx, mais avec
        // l'emoji de repli si `repli` en fournit un — jamais un hotspot invisible au hasard.
        const { position, repli } = hotspotChambre(scene, objet.id);
        const emojiEffectif = repli ?? objet.emoji;
        // ⟦C31⟧ Plus de plaque nom + coût dans la scène (décision de Matt du 16/09, docs/
        // chantiers/C31-sans-etiquettes.md : « pas besoin des étiquettes [...] vu que tu as déjà
        // les boutons d'action en bas ») : `nom`/`cout` ne nourrissent plus que le nom accessible
        // (`libelleAccessibleObjet`, posé en `aria-label` plus bas), jamais un texte visible.
        // `detail` reste : ce n'est pas une étiquette nom + coût, c'est un contenu qui se LIT EN
        // PLACE (⟦A-7 décision 6⟧) et qui n'existe nulle part ailleurs à l'écran — vérifié pour
        // les tendances du panneau à post-it (aucun doublon dans le bandeau du Journal, rapport
        // du chantier), l'étagère (rente des evergreens) restant hors périmètre de ce chantier.
        const contenu = (
          <>
            {emojiEffectif ? (
              <span className="objet-emoji" aria-hidden="true">
                {emojiEffectif}
              </span>
            ) : null}
            {objet.detail ? <span className="objet-detail">{objet.detail}</span> : null}
          </>
        );
        // ⟦C31⟧ Une « plaque » qui ne montre plus rien (ni emoji ni détail — le cas de lit,
        // bureau, fenêtre : peints dans le fond, sans emoji propre, sans détail) ne doit plus se
        // monter du tout : `.objet` a un padding et une bordure pointillée (transparente au
        // repos, dorée au survol via `.objet-etat-survol`) — un cadre vide resterait une
        // étiquette fantôme, l'exact défaut que ce chantier corrige. Voir son usage plus bas.
        const contenuVisible = Boolean(emojiEffectif) || Boolean(objet.detail);
        // ⟦C26 ter · point 2⟧ **Le contenu au repos grandit vers le côté libre.** Une ancre posée
        // dans la moitié DROITE de l'image n'a jamais de marge à droite avant le bord de la
        // scène, quelle que soit la taille d'écran — utile tant qu'un objet affiche encore un
        // emoji ou un détail (l'étagère, par exemple, à 77 % de l'image). Purement géométrique
        // (position brute de l'objet dans l'image, jamais une mesure de boîte au rendu).
        const coteGaucheRepos = (position?.x ?? objet.x) > 50;
        // ⟦C21 bis · point 1⟧ Reconverti en % de boîte (`positionDansBoite`) : le
        // hotspot reste calé sur le MEUBLE dessiné, pas sur un cadre qui a pu recadrer l'image.
        // ⟦C21 bis · point 2⟧ Le décalage d'étiquette (lit) s'applique AVANT la conversion,
        // encore en % d'image — il doit rester correct après recadrage, comme le hotspot lui-même.
        // ⟦C26⟧ Sauf sur une scène à planches d'état : l'avatar n'y est jamais superposé, le
        // décalage anti-chevauchement n'a plus lieu d'être (voir le commentaire de la table).
        const decalage = scenePossedePlanchesEtat(scene) ? undefined : DECALAGE_ETIQUETTE[objet.id];
        const positionBoite = positionDansBoite(
          {
            x: (position?.x ?? objet.x) + (decalage?.x ?? 0),
            y: (position?.y ?? objet.y) + (decalage?.y ?? 0),
          },
          boite,
          ajustementEffectif,
          ancrageVertical,
        );
        // ⟦C30 · lot E · point 3⟧ Le décalage anti-chevauchement (0 par défaut, voir le
        // commentaire de `decalagesBoite` plus haut) s'applique à TOUT objet, pas seulement ceux
        // qui passent par la branche `emprise` ci-dessous : un objet sans emprise mesurée (le
        // téléphone) peut, lui aussi, tomber derrière la boîte de Matt selon la taille d'écran.
        const style = {
          left: `${positionBoite.x}%`,
          top: `${positionBoite.y - decalageBoitePct(objet.id)}%`,
          ["--taille" as string]: String(objet.taille ?? 1),
        };

        // ⟦C31⟧ **La plaque qui migrait hors du personnage (⟦C26 bis · point 1⟧, `ancreEtiquetteEtat`)
        // a disparu avec elle.** Le mécanisme existait pour éloigner la plaque VISIBLE (nom +
        // coût) du visage du personnage peint sur `lit`/`bureau` — depuis que cette plaque ne
        // montre plus rien (décision de Matt du 16/09), il n'y a plus rien à éloigner : `lit` et
        // `bureau` n'ont ni emoji ni détail, leur « plaque » ne se monte donc plus jamais
        // (`contenuVisible`, voir plus haut). `ancreEtiquetteEtat`/`ANCRE_ETIQUETTE_ETAT`
        // (`game/chambre.ts`) sont retirés avec lui, code mort une fois cette condition tombée.
        //
        // ⟦C30 · lot E · point 1⟧ **L'emprise mesurée (`game/chambre.ts`) agrandit le hotspot au
        // meuble RÉELLEMENT dessiné** — un hotspot INVISIBLE, dimensionné et centré sur `emprise`
        // (jamais l'ancre de clic, qui n'est en général pas le centre géométrique du meuble), et
        // un éventuel contenu visible (`contenuVisible`) qui reste au hotspot, comme n'importe
        // quel autre objet. `telephone` n'a pas d'entrée dans `empriseObjetChambre` (aucun objet
        // dessiné à mesurer sur cette planche, voir son commentaire) : il retombe sur les
        // branches inchangées plus bas.
        const emprise = empriseObjetChambre(scene, objet.id);
        if (emprise && (objet.action || estPoseEtat(objet.id))) {
          const hotspotEmprise = styleEmpriseHotspot(emprise, boite, ajustementEffectif, ancrageVertical);
          const styleHotspot = {
            left: `${hotspotEmprise.left}%`,
            top: `${hotspotEmprise.top}%`,
            width: `${hotspotEmprise.largeurPx}px`,
            height: `${hotspotEmprise.hauteurPx}px`,
            maxWidth: "none",
            padding: 0,
            boxSizing: "border-box" as const,
          };
          // Le survol/focus se lit sur LE HOTSPOT (`hoverId`, déjà la source du calque avatar),
          // jamais un second `:hover` CSS sur le contenu visible éventuel, qui n'est plus
          // l'élément survolé : il est purement décoratif (`aria-hidden`), le hotspot invisible
          // porte seul l'accessibilité (bouton réel, `aria-label`, focus clavier violet ⟦A-143⟧).
          const survole = hoverEffectif === objet.id;
          const estBouton = Boolean(objet.action);
          const libelle = libelleAccessibleObjet(objet);
          return (
            <Fragment key={objet.id}>
              {estBouton ? (
                <button
                  type="button"
                  className="objet objet-cliquable objet-hotspot-etat"
                  style={styleHotspot}
                  disabled={objet.desactive}
                  aria-label={libelle}
                  data-objet={objet.id}
                  data-objet-role="hotspot"
                  onClick={objet.action}
                  onMouseEnter={() => setHoverId(objet.id)}
                  onMouseLeave={() => setHoverId((id) => (id === objet.id ? null : id))}
                  onFocus={() => setHoverId(objet.id)}
                  onBlur={() => setHoverId((id) => (id === objet.id ? null : id))}
                />
              ) : (
                // Survolable/focusable sans être cliquable (le panneau à post-it, ⟦A-7 décision
                // 6⟧) : même patron que la branche `estPoseEtat` sans emprise plus bas, en plus
                // grand. Pas de `.objet-cliquable` (jamais de curseur pointeur ni de halo doré sur
                // un objet qui ne fait rien au clic, comportement inchangé pour le panneau).
                <div
                  className="objet objet-hotspot-etat"
                  style={styleHotspot}
                  tabIndex={0}
                  aria-label={libelle}
                  data-objet={objet.id}
                  data-objet-role="hotspot"
                  onMouseEnter={() => setHoverId(objet.id)}
                  onMouseLeave={() => setHoverId((id) => (id === objet.id ? null : id))}
                  onFocus={() => setHoverId(objet.id)}
                  onBlur={() => setHoverId((id) => (id === objet.id ? null : id))}
                />
              )}
              {/* ⟦C31⟧ Rien à afficher (`lit`, `bureau`, `fenetre` : plus de plaque, aucun emoji
                  ni détail) ⇒ aucun élément monté — jamais un cadre vide qui redeviendrait une
                  étiquette fantôme au survol/focus (voir `contenuVisible` plus haut). Seul
                  `tableau` (les tendances, tant qu'elles ne sont pas dupliquées ailleurs, voir le
                  rapport du chantier) affiche encore quelque chose ici aujourd'hui. */}
              {contenuVisible ? (
                <div
                  className={`objet objet-${objet.id}${coteGaucheRepos ? " objet-cote-gauche" : ""}${survole ? " objet-etat-survol" : ""}${objet.desactive ? " objet-etat-desactive" : ""}`}
                  style={{
                    left: `${positionBoite.x}%`,
                    top: `${positionBoite.y - decalageBoitePct(objet.id)}%`,
                    ["--taille" as string]: String(objet.taille ?? 1),
                  }}
                  aria-hidden="true"
                  data-objet={objet.id}
                  data-objet-role="plaque"
                >
                  {contenu}
                </div>
              ) : null}
            </Fragment>
          );
        }

        // ⟦Micro-correctif reskin 8-bit⟧ `objet-${id}` permet à app.css de cibler UN objet
        // précis (bureau, lit) sans toucher aux autres — nécessaire pour la pastille discrète
        // des hotspots devenus invisibles.
        if (objet.action) {
          return (
            <button
              key={objet.id}
              type="button"
              className={`objet objet-cliquable objet-${objet.id}${coteGaucheRepos ? " objet-cote-gauche" : ""}`}
              style={style}
              disabled={objet.desactive}
              // ⟦C31⟧ Sans plaque visible, le nom accessible du bouton ne peut plus venir de son
              // contenu texte (il n'y en a plus forcément) : posé explicitement, nom + coût.
              aria-label={libelleAccessibleObjet(objet)}
              data-objet={objet.id}
              onClick={objet.action}
              // ⟦C16 §1⟧ Le calque objet (le halo `:hover`/`:focus-visible`, `app.css`) ET le
              // calque avatar (`hoverId`, plus haut) partagent le MÊME survol — souris et clavier
              // (`onFocus`/`onBlur`) déplacent l'avatar de la même façon, jamais seulement l'un
              // des deux. Le `id === objet.id` en sortie protège contre un ordre focus→leave
              // inversé entre deux objets qui se chevauchent.
              onMouseEnter={() => setHoverId(objet.id)}
              onMouseLeave={() => setHoverId((id) => (id === objet.id ? null : id))}
              onFocus={() => setHoverId(objet.id)}
              onBlur={() => setHoverId((id) => (id === objet.id ? null : id))}
            >
              {contenu}
            </button>
          );
        }

        // ⟦C29 · intégration · correctif tour 1⟧ Un objet SANS action (⟦A-7 décision 6⟧ : son
        // contenu se lit en place, jamais de clic, le cas du tableau des tendances) tombait
        // jusqu'ici dans le `<div>` muet ci-dessous, MÊME quand il a une planche d'état
        // (`estPoseEtat`, `game/chambre.ts` : lit/bureau/fenêtre/tableau/commode/téléphone) : le
        // survol ne posait jamais `hoverId`, donc `poseEtatSurvol` plus haut ne le voyait
        // jamais, sa planche (le zoom rapproché) ne s'affichait donc JAMAIS, ni à la souris ni
        // au clavier (relevé par un vérificateur du chantier C29, lot A, sur le tableau).
        // Corrigé en distinguant désormais deux choses que l'ancien `objet.action ? bouton :
        // div` confondait : « cliquable » (un bouton, une action) et « survolable / focusable »
        // (montre sa planche). Un objet à planche d'état mais sans action reste un `<div>`,
        // jamais un `role="button"`, aucune action au clic, aucun `onClick`, mais gagne le
        // MÊME survol/focus que les objets cliquables : `tabIndex={0}` (le focus clavier
        // fonctionne sans rôle de bouton, l'anneau `:focus-visible` générique, `app.css`, s'y
        // pose déjà tout seul) et les quatre gestionnaires `onMouseEnter`/`onMouseLeave`/
        // `onFocus`/`onBlur` qui alimentent `hoverId`, identiques à ceux du bouton ci-dessus.
        // Un objet sans planche d'état (la grande majorité des décors) n'est pas concerné :
        // `estPoseEtat` le filtre, aucun changement de comportement pour eux.
        if (estPoseEtat(objet.id)) {
          return (
            <div
              key={objet.id}
              className={`objet objet-${objet.id}${coteGaucheRepos ? " objet-cote-gauche" : ""}`}
              style={style}
              tabIndex={0}
              aria-label={libelleAccessibleObjet(objet)}
              data-objet={objet.id}
              onMouseEnter={() => setHoverId(objet.id)}
              onMouseLeave={() => setHoverId((id) => (id === objet.id ? null : id))}
              onFocus={() => setHoverId(objet.id)}
              onBlur={() => setHoverId((id) => (id === objet.id ? null : id))}
            >
              {contenu}
            </div>
          );
        }

        return (
          <div key={objet.id} className={`objet objet-${objet.id}${coteGaucheRepos ? " objet-cote-gauche" : ""}`} style={style}>
            {contenu}
          </div>
        );
      })}

      {/* Le personnage, sprite pixel posé sur le fond (⟦C14 · A-133⟧ — remplace l'emoji
          placeholder de §8.1). Il fatigue avec la santé mentale — et ⟦A-12 §3⟧ ses couleurs se
          DRAINENT en zone rouge : « la désaturation devient le langage de l'échec », et c'est
          le seul endroit du jeu où la saturation baisse. L'écran entier se délave déjà (§7.5,
          `.zone-rouge`) ; l'avatar va plus loin — presque gris, trait conservé — parce que
          c'est LUI qui brûle, pas la chambre. Le filtre s'applique au sprite exactement comme
          il s'appliquait à l'emoji : `.avatar-fatigue`/`.avatar-rouge` visent le conteneur.
          ⟦C15⟧ Le SPRITE lui-même change aussi désormais (genre + frais/fatigue/burnout,
          `game/heros.ts`) — ce filtre CSS continue de s'appliquer PAR-DESSUS, les deux langages
          de l'échec se cumulent plutôt que de se remplacer.

          ⟦C16 §1 · C23 §3⟧ Au survol du bureau ou du lit (`estObjetAvecSuivi`), l'avatar se
          déplace vers l'objet mais reste `idle` (debout), retour de Matt, la 3e fois : « le
          personnage qui va au bureau ne matche pas ». Seule la fenêtre garde une pose de survol
          dédiée (`spriteSurvol`). Au repos (aucun survol, ou un objet sans suivi), rien ne
          change de son point de vue : c'est très exactement « au repos, l'avatar reprend sa
          position et sa pose neutres » (⟦C21 bis · point 1⟧) : `left`/`top` s'écrivent maintenant
          TOUJOURS en JS (`positionAvatar`, ci-dessus), repos compris. La transition de position
          (~150 ms) vit dans `app.css`, coupée par `prefers-reduced-motion` comme tout le reste
          de l'app.

          ⟦C23 §3⟧ Pendant `sommeil`/`travail` (pas `victoire`), l'avatar debout DISPARAÎT
          (`.avatar-chambre-cachee`, fondu court) : les sprites couché/assis ne matchaient ni le
          lit ni le bureau des planches, et aucun calage ne le rattrape : il « entre dans le
          meuble » à la place (bulles ci-dessous), Système D.

          ⟦C26 · A-150 · C34⟧ Le sprite et les effets d'écran/REC ne se montent QUE si aucun
          calque de pose ne peut couvrir la situation (`avatarSuperpose`, plus haut) : quand la
          scène est en calques ET le genre connu, le personnage est déjà porté par le fond ou le
          calque de pose (ci-dessus), un sprite superposé en plus serait un doublon, pas un
          complément.

          ⟦C26 bis · point 3 bis⟧ Les « z z z », eux, sortent de cette garde (voir plus bas) :
          contrairement au sprite, ils ne font PAS double emploi avec une planche peinte, c'est
          justement ce qui manquait pour qu'on VOIE qu'on dort pendant la tenue de 1,8 s de
          `dormir()` (`Studio.tsx`), le personnage peint étant immobile, sans indice de sommeil
          propre une fois la planche affichée plus d'une fraction de seconde. */}
      {avatarSuperpose ? (
        <>
          <div
            className={`avatar-chambre avatar-${zoneMental}${avatarDeplace ? " avatar-chambre-survol" : ""}${poseCachee ? " avatar-chambre-cachee" : ""}`}
            style={{ left: `${positionAvatar.x}%`, top: `${positionAvatar.y}%` }}
            aria-label="Toi, devant ton écran"
          >
            {/* image-rendering: pixelated est posé en CSS (`.avatar-chambre img`) — ⟦C14 critère 2⟧. */}
            <img
              src={spriteSurvol ?? spriteHeros(poseCachee ? "idle" : pose, state.genre, zoneMental)}
              alt=""
              aria-hidden="true"
            />
          </div>

          {/* ⟦C23 §3⟧ Pendant `travail` : l'écran du bureau s'allume (rectangle pixel qui clignote
              doucement en bleu/blanc, position par planche, `positionEcranBureau`) et une bulle
              « ▶ REC » rouge, ancrée sur le hotspot bureau lui-même, absente si la planche ne
              dessine pas d'écran distinct (n2, n5, cf. `game/chambre.ts`), la bulle REC reste alors
              la seule preuve visuelle. */}
          {positionEcranEffet ? (
            <div
              className="effet-ecran-allume"
              style={{ left: `${positionEcranEffet.x}%`, top: `${positionEcranEffet.y}%` }}
              aria-hidden="true"
            />
          ) : null}
          {positionBureauEffet ? (
            <div
              className="effet-rec"
              style={{ left: `${positionBureauEffet.x}%`, top: `${positionBureauEffet.y}%` }}
              aria-hidden="true"
            >
              ▶ REC
            </div>
          ) : null}
        </>
      ) : null}

      {/* ⟦C26 bis · point 3 bis⟧ « z z z » : pendant `sommeil`, flotte au-dessus du lit, trois
          lettres qui montent en boucle (CSS pur, `app.css`), coupé comme toute animation par
          `prefers-reduced-motion`, MÊME quand une planche d'état peint déjà le personnage
          endormi (⟦A-150⟧ les avait retirés en même temps que le sprite, dans le même bloc :
          eux seuls sont sortis de cette garde, voir le commentaire au-dessus). */}
      {positionLitEffet ? (
        <div
          className="effet-sommeil"
          style={{ left: `${positionLitEffet.x}%`, top: `${positionLitEffet.y}%` }}
          aria-hidden="true"
        >
          <span>z</span>
          <span>z</span>
          <span>z</span>
        </div>
      ) : null}

      {/* ⟦A-5 étage 3 · C40 · A-163⟧ « Ma ligne » ne s'affiche plus ICI, au milieu de la scène
          (retour de Matt, 18/09 : « ça va pas, cette grosse fenêtre au milieu ») — voir
          `IndicateurMaLigne`, exporté plus bas, posé par `Studio.tsx` dans la fenêtre « Ma
          chaîne », près des jauges. Toujours lisible sans clic (A-7 décision 6), seul
          l'EMPLACEMENT change. */}
    </div>
  );
}

/**
 * ⟦A-5 étage 3 · A-62 · C40 · A-163⟧ **« Ma ligne » — sortie du milieu de la scène.** Retour de
 * Matt (18/09) : « il faudrait trouver quelque chose pour l'alignement, parce que ça va pas,
 * cette grosse fenêtre au milieu. » L'ancien `.poster` (position ABSOLUE, 45 %/15 % de la scène,
 * `app.css`) posait un rectangle blanc en plein milieu du diorama, par-dessus tout ce qui s'y
 * jouait — jamais anodin sur une scène pensée pour se lire d'un coup d'œil, exactement le défaut
 * qu'A-7 décision 6 évitait déjà pour les jauges (« restent lisibles en permanence, dans "Ma
 * chaîne"/"Journal" plutôt qu'en overlay flottant sur le diorama »). `Ma ligne` était le seul
 * indicateur permanent resté en overlay flottant : ce chantier aligne son sort sur celui des
 * jauges, dans le MÊME endroit (⟦C21 · A-146⟧).
 *
 * Composant à part (et non plus un fragment de JSX dans `Chambre`) : `Studio.tsx` le pose dans le
 * CORPS de la fenêtre « Ma chaîne » (`corpsChaine`, près de `.jauges`), pas dans la scène — deux
 * arbres de composants différents, `<Chambre>` n'a plus les moyens de le faire lui-même.
 * L'information reste lisible SANS clic (⟦A-7 décision 6⟧ inchangée) : seul l'EMPLACEMENT change,
 * jamais le contenu ni le geste pour le lire. Gatée sur `ouvert(state, "image")`, comme avant ce
 * chantier — `null` avant ce déblocage, pas un espace vide réservé (⟦A-5⟧ « ce qui n'est pas
 * déverrouillé est absent »).
 *
 * ⟦C60 · A-177⟧ **L'alignement est coupé : l'indicateur n'existe plus.** L'étage « image » (le poster, la
 * ligne lumière ou chaos) disparaît de l'escalier (`absentSi: "alignement"`, `game/escalier.ts`), donc
 * `ouvert(state, "image")` rend `false` et ce composant ne rend rien : la même porte, le même code, rien
 * de supprimé. Rallumé, « Ma ligne » revient à sa place.
 */
export function IndicateurMaLigne({ state }: { state: GameState }) {
  const bandeId = bandeDeLaChaine(state);
  const bande = bandeId === null ? null : contenuJeu().axeProduit.bandes.find((b) => b.id === bandeId);
  if (!ouvert(state, "image")) return null;
  return (
    <div className="indicateur-ligne" title="L'image de ta chaîne : ce que tes vidéos racontent de toi">
      <span className="indicateur-ligne-titre">Ma ligne</span>
      <div className="indicateur-ligne-piste">
        <div
          className="indicateur-ligne-curseur"
          style={{ left: `${((state.channel_image + 100) / 200) * 100}%` }}
        />
      </div>
      <span className="indicateur-ligne-valeur">
        {badgeAlignement(state.channel_image).icone} {badgeAlignement(state.channel_image).libelle}{" "}
        ({state.channel_image})
      </span>
      {/* ⟦SPEC-AXE-PRODUIT §9.2 question 1 / A-62⟧ **L'axe Produit ↔ Authentique — le libellé de
          bande, et rien d'autre.** Voir l'historique complet de cette décision dans le chantier
          C21 (avant l'extraction de ce composant, ⟦C40⟧) : un curseur chiffré inviterait à
          optimiser un axe qu'on ne joue justement pas — on dit « chaîne artisanale », jamais
          « −72 ». `bande === null` ⇒ rien du tout (avant le Grand Saut ⟦A-1⟧, rien à mesurer). */}
      {bande ? (
        <span className="indicateur-ligne-facon" title={bande.description}>
          {bande.icone} {bande.libelle}
        </span>
      ) : null}
    </div>
  );
}
